import React, { useState, useEffect } from "react";
import { Play, Video } from "lucide-react";
import { videoForLesson } from "@/data/lessonVideos";
import SafeVideoPlayer from "@/components/SafeVideoPlayer";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * <LessonVideo /> — kid-safe embedded explainer video for a lesson.
 *
 * Uses youtube-nocookie with a tight parameter set (see
 * `embedUrlFor`) so children stay inside the app, without related-
 * video overlays, keyboard shortcuts or channel branding.
 *
 * The player is **click-to-load** — no video is fetched until the
 * child taps Play. This keeps the lesson page fast on slow networks
 * and avoids YouTube analytics loading in the background.
 *
 * If we don't have a curated video for the current lesson,
 * the component renders nothing (LessonReader continues to render
 * the AI Teacher pill as the visual/audio alternative).
 *
 * Feb 2026 · Every candidate video is scored by an LLM against the
 * lesson's topic / outline / tier via `/api/videos/relevance-check`.
 * If the verdict is `relevant: false`, the video is hidden so kids
 * never see off-topic clips. Verdicts are cached both server-side
 * (Mongo `lesson_video_verdicts`) and client-side (localStorage) so
 * repeated visits are free.
 */

/**
 * Local relevance cache: 'yes' / 'no' / 'unknown'. Key encodes the
 * lesson_code + video_id + tier (tier matters — the SAME video may be
 * on-topic for explorer but not for seedling). Persisted so a page
 * refresh doesn't re-hit the endpoint.
 */
function _cacheKey(code, videoId, tier) {
  return `mwq-lv-verdict::${code}::${videoId}::${tier || ""}`;
}
function _readCache(code, videoId, tier) {
  try {
    return localStorage.getItem(_cacheKey(code, videoId, tier)) || null;
  } catch { return null; }
}
function _writeCache(code, videoId, tier, verdict) {
  try {
    localStorage.setItem(_cacheKey(code, videoId, tier), verdict);
  } catch { /* quota-full → silent */ }
}

function _curatedKey(code, tier) {
  return `mwq-lv-curated::${code}::${tier || ""}`;
}
function _readCurated(code, tier) {
  try {
    const raw = localStorage.getItem(_curatedKey(code, tier));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function _writeCurated(code, tier, video) {
  try {
    localStorage.setItem(_curatedKey(code, tier), JSON.stringify(video || null));
  } catch { /* quota-full */ }
}

export default function LessonVideo({ code, subject, ageTier, topic }) {
  const [playing, setPlaying] = useState(false);
  // Priority:
  //   1. Server-curated video from /api/lessons/{code}/curated-video
  //      (already LLM-verified against the lesson, no client gate needed)
  //   2. Static-catalog pick from `videoForLesson()`, gated client-side
  const staticVideo = videoForLesson({ code, subject, ageTier, topic });
  // Read curated from localStorage synchronously (no flicker on repeat visits).
  const initialCurated = code && ageTier ? _readCurated(code, ageTier) : null;
  const [curated, setCurated] = useState(initialCurated); // null=loading, false/{}=none, obj=found
  useEffect(() => {
    let cancelled = false;
    if (!code || !ageTier) { setCurated(false); return undefined; }
    // If we have a cached hit, don't refetch — the value is warmed
    // by the overnight curator so it never changes mid-session.
    if (initialCurated && initialCurated.id) return undefined;
    (async () => {
      try {
        const r = await fetch(`${API}/api/lessons/${encodeURIComponent(code)}/curated-video?tier=${encodeURIComponent(ageTier)}`);
        if (!r.ok) { if (!cancelled) setCurated(false); return; }
        const j = await r.json();
        if (cancelled) return;
        const v = j.video || false;
        _writeCurated(code, ageTier, v || null);
        setCurated(v);
      } catch { if (!cancelled) setCurated(false); }
    })();
    return () => { cancelled = true; };
  }, [code, ageTier, initialCurated]);

  // Curated wins outright when present — it's already LLM-verified
  // and channel-safe, so no client-side gate roundtrip is needed.
  const video = curated || staticVideo;
  const isCurated = !!curated;

  const cached = video ? _readCache(code, video.id, ageTier) : null;
  const [verdict, setVerdict] = useState(cached || "yes");

  useEffect(() => {
    let cancelled = false;
    if (!video || !code) return undefined;
    // Curated videos skip the client-side gate (already verified).
    if (isCurated) { setVerdict("yes"); return undefined; }
    // Skip if we already have a cached verdict.
    if (cached === "yes" || cached === "no") return undefined;
    // Fire-and-forget LLM check. Silently ignore any error.
    (async () => {
      try {
        const r = await fetch(`${API}/api/videos/relevance-check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lesson_code: code,
            video_id: video.id,
            video_title: video.title,
            video_channel: video.channel || "",
            tier: ageTier || "",
          }),
        });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        const v = j.relevant === true ? "yes" : "no";
        _writeCache(code, video.id, ageTier, v);
        setVerdict(v);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [video, code, ageTier, cached, isCurated]);

  // Wait for the curated fetch to resolve before rendering — this
  // avoids a flash of the static video that would then be replaced.
  if (curated === null) return null;
  if (!video) return null;
  if (verdict === "no") return null; // LLM flagged off-topic — hide it.

  return (
    <div
      className="mt-6 rounded-3xl border-4 border-slate-900 bg-slate-900 shadow-[6px_6px_0_0_rgba(15,23,42,1)] overflow-hidden"
      data-testid="lesson-video"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2 bg-slate-800 text-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <Video size={14} strokeWidth={2.5} className="shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Watch this</p>
            <p className="text-sm font-semibold truncate">{video.title}</p>
          </div>
        </div>
        <span className="text-[10px] font-semibold text-slate-400 shrink-0">{video.channel} · {video.duration}</span>
      </div>
      <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
        {playing ? (
          // Provider-agnostic renderer — accepts owned MP4/HLS entries
          // and falls back to youtube-nocookie automatically.
          <SafeVideoPlayer video={video} autoPlay testId="lesson-video" />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            data-testid="lesson-video-play"
            aria-label={`Play video: ${video.title}`}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 group focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400"
            style={{
              backgroundImage: `url(${video.poster || `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <span className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors" aria-hidden />
            <span className="relative inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-400 border-4 border-white shadow-[0_4px_0_0_rgba(15,23,42,0.6)] transition-transform group-hover:scale-105">
              <Play size={26} strokeWidth={3} className="text-slate-900 ml-1" aria-hidden />
            </span>
            <span className="relative text-white font-bold text-sm">Tap to watch</span>
          </button>
        )}
      </div>
      {/* Feb 2026 · Cycle 55 — external "Try YouTube →" fallback link
          removed on user request. Sending kids off to YouTube search
          creates a distraction and defeats the point of curated
          lessons. If the embedded video is unavailable, LessonVideos
          below shows other curated alternatives that all play in the
          same in-app modal. */}
    </div>
  );
}
